import { env } from '@/env';
import { getIsMobilePlatform } from '@/utils/device';

declare global {
  interface Window {
    Forethought: (key: string, value: string) => void;
  }
}

const CHATBOT_SCRIPT_ID = 'forethought-widget-embed-script';
const CHATBOT_WIDGET_BUTTON_IMAGE =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">' +
      '<circle cx="12" cy="12" r="12" fill="#04747A"/>' +
      '<g fill="#ffffff" transform="translate(4,4) scale(0.667)">' +
      '<path opacity=".7" d="M19,3H5C3.343,3,2,4.343,2,6v13v0.828c0,1.746,1.974,2.762,3.395,1.747l2.822-2.016C8.726,19.195,9.336,19,9.961,19H19c1.657,0,3-1.343,3-3V6C22,4.343,20.657,3,19,3z"/>' +
      '<path d="M15,10H9c-0.553,0-1-0.448-1-1s0.447-1,1-1h6c0.553,0,1,0.448,1,1S15.553,10,15,10z"/>' +
      '<path d="M13,14H9c-0.553,0-1-0.448-1-1s0.447-1,1-1h4c0.553,0,1,0.448,1,1S13.553,14,13,14z"/>' +
      '</g>' +
      '</svg>'
  );
const CHATBOT_WORKFLOW_TAG = 'happy_hearts';

const MOBILE_OFFSET_Y = '120px';
const DESKTOP_OFFSET_Y = '90px';

type ChatBotConfig = Map<string, string>;

const offsetY = getIsMobilePlatform() ? MOBILE_OFFSET_Y : DESKTOP_OFFSET_Y;
const positionY = getIsMobilePlatform() ? 'top' : 'bottom';

const defaultAttributes = new Map<string, string>([
  ['src', 'https://solve-widget.forethought.ai/embed.js'],
  ['id', CHATBOT_SCRIPT_ID],
  ['data-api-key', env.FORETHOUGHT_API_KEY],
  ['data-ft-workflow-tag', CHATBOT_WORKFLOW_TAG],
  ['config-ft-widget-button-image', CHATBOT_WIDGET_BUTTON_IMAGE],
  ['emit-tracking-events', 'true'],
  ['offset-x', '0px'],
  ['offset-y', offsetY],
  ['position-y', positionY],
  ['position-x', 'right'],
  ['data-ft-embed-script-language', 'en'],
  // ['hidden', 'true'], // TODO future - Uncomment this line to hide chatbot by default
]);

const isAttributesEqual = (existingScriptTag: HTMLScriptElement, newConfig: ChatBotConfig) => {
  for (const [key, value] of newConfig) {
    if (existingScriptTag.getAttribute(key) !== value) {
      return false;
    }
  }
  return true;
};

let retryTimeoutPointer: number;
let existingScriptTag: HTMLScriptElement | null = null;

export const chatBotManager = {
  init: (config: Map<string, string> = new Map()) => {
    const combinedConfig = new Map([...defaultAttributes, ...config]);
    if (existingScriptTag && isAttributesEqual(existingScriptTag, combinedConfig)) {
      return;
    }

    existingScriptTag?.remove();
    if (document.querySelector(`#${CHATBOT_SCRIPT_ID}`)) {
      document.querySelector(`#${CHATBOT_SCRIPT_ID}`)?.remove();
    }

    const script = document.createElement('script');
    for (const [key, value] of combinedConfig) {
      script.setAttribute(key, value);
    }
    existingScriptTag = script;

    document.body.prepend(script);
  },
  open: () => {
    chatBotManager.setWidgetVisibility(true);
    if (window.Forethought) {
      window.Forethought('widget', 'open');
    }
  },
  close: () => {
    if (window.Forethought) {
      window.Forethought('widget', 'close');
    }
  },
  setWidgetVisibility: (isVisible: boolean) => {
    if (window.Forethought) {
      window.Forethought('widget', isVisible ? 'show' : 'hide');
    } else {
      clearTimeout(retryTimeoutPointer);
      retryTimeoutPointer = window.setTimeout(() => {
        chatBotManager.setWidgetVisibility(isVisible);
      }, 1000);
    }
  },
};
